import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateGroupNameConflict } from 'modules/manager/domain/template-name-conflict';
import MediaManagerTemplateGroupForm, {
  TemplateGroupFormValues,
} from 'modules/manager/ui/components/forms/media-manager-template-group-form';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

interface Args {
  groups: MediaManagerTemplateGroup[];
  onUpdated: () => void;
}

function useRenameTemplateGroupModal({ groups, onUpdated }: Args) {
  const { t } = useLingui();
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setError(null);
    setGroup(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const existingNames = useMemo(
    () => groups.filter((item) => item.id !== group?.id).map((item) => item.name.trim()),
    [groups, group],
  );

  const onSubmit = useCallback(
    async (values: TemplateGroupFormValues) => {
      if (!group) return;
      setIsLoading(true);
      setError(null);
      try {
        await mediaManagerRepository.updateTemplateGroup({ id: group.id, name: values.name.trim() });
        onUpdated();
        close();
      } catch (err) {
        console.error('Failed to rename media manager template group:', err);
        setError(
          isTemplateGroupNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось переименовать группу, попробуйте ещё раз`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [group, onUpdated, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Переименовать группу</Trans>
        </UDText>
        <MediaManagerTemplateGroupForm
          key={group?.id ?? 'none'}
          initialValues={{ name: group?.name ?? '' }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={<Trans>Сохранить</Trans>}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useRenameTemplateGroupModal;
